import { loadSettings, saveSettings, profileFor, Settings, QualityPreset } from "./settings";
import { SHIP_VARIANTS, unlockedVariants } from "./ship-variants";
import { TRAIL_PALETTES, isPaletteUnlocked } from "./trail-variants";
import { getProfile, rankForXp } from "../site/profile";
import { getBest } from "./personal-best";
import { ACHIEVEMENTS, achievementProgress } from "./achievements";
import { codexHtml } from "./codex";
import { FocusTrap } from "./focus-trap";

export interface SettingsDrawerCallbacks {
  onChange: (s: Settings) => void;
}

export class SettingsDrawer {
  private root: HTMLDivElement;
  private trigger: HTMLButtonElement;
  private panel: HTMLDivElement;
  private settings: Settings;
  private cb: SettingsDrawerCallbacks;
  private trap = new FocusTrap();

  constructor(parent: HTMLElement, cb: SettingsDrawerCallbacks) {
    this.cb = cb;
    this.settings = loadSettings();

    this.root = document.createElement("div");
    this.root.className = "settings-root";

    this.trigger = document.createElement("button");
    this.trigger.className = "settings-trigger";
    this.trigger.setAttribute("aria-label", "Open settings");
    this.trigger.innerHTML = gearSvg();
    this.trigger.addEventListener("click", () => this.toggle());
    this.root.appendChild(this.trigger);

    this.panel = document.createElement("div");
    this.panel.className = "settings-panel";
    this.panel.setAttribute("role", "dialog");
    this.panel.setAttribute("aria-label", "Settings");
    this.panel.hidden = true;
    this.panel.innerHTML = this.template();
    this.root.appendChild(this.panel);

    parent.appendChild(this.root);

    this.wire();
    // Apply on construction so the engine reflects persisted values from the start.
    this.cb.onChange(this.settings);
  }

  private toggle() {
    this.panel.hidden = !this.panel.hidden;
    this.trigger.setAttribute("aria-expanded", String(!this.panel.hidden));
    if (!this.panel.hidden) {
      this.trap.activate(this.panel, () => this.toggle());
    } else {
      this.trap.deactivate();
    }
  }

  get current(): Settings {
    return this.settings;
  }

  /**
   * Render ship options grouped by unlocked vs. locked. Best score lookup
   * uses the saved handle's PB to drive score-based gates; achievement
   * gates use the global earned set in localStorage.
   */
  private shipOptionsHtml(): string {
    const handle = localStorage.getItem("aether.handle") || "";
    const best = handle ? getBest(handle) : 0;
    let earned: Set<string> = new Set();
    try {
      earned = new Set(Object.keys(JSON.parse(localStorage.getItem("aether.achievements") || "{}")));
    } catch { /* defaults to empty set */ }
    const unlocked = new Set(unlockedVariants(best, earned).map((v) => v.id));
    return SHIP_VARIANTS.map((v) => {
      const isUnlocked = unlocked.has(v.id);
      const label = isUnlocked ? v.name : `${v.name} 🔒`;
      const disabled = isUnlocked ? "" : "disabled";
      return `<option value="${v.id}" ${disabled}>${label}</option>`;
    }).join("");
  }

  /** Render trail-palette options. Lock label mirrors ship-variant
   *  convention; locked rows are kept visible so the player can see what
   *  rank they need to unlock the next one. */
  private trailOptionsHtml(): string {
    const rank = rankForXp(getProfile().totalXp).name;
    return TRAIL_PALETTES.map((p) => {
      const unlocked = isPaletteUnlocked(p, rank);
      const label = unlocked ? p.name : `${p.name} 🔒 ${p.unlockRank}`;
      const disabled = unlocked ? "" : "disabled";
      return `<option value="${p.id}" ${disabled}>${label}</option>`;
    }).join("");
  }

  private earnedSet(): Set<string> {
    try {
      return new Set(Object.keys(JSON.parse(localStorage.getItem("aether.achievements") || "{}")));
    } catch {
      return new Set();
    }
  }

  private achievementCount(): string {
    const earned = this.earnedSet();
    return `${earned.size} / ${ACHIEVEMENTS.length}`;
  }

  private achievementListHtml(): string {
    const earned = this.earnedSet();
    return ACHIEVEMENTS.map((a) => {
      const isEarned = earned.has(a.id);
      const cls = isEarned ? "earned" : "locked";
      const icon = isEarned ? "✓" : "·";
      // Progress bar for locked achievements with a known target.
      let progressBar = "";
      if (!isEarned && a.target) {
        const v = Math.min(a.target, achievementProgress(a.id));
        const pct = Math.round((v / a.target) * 100);
        progressBar = `<span class="ach-progress" aria-label="${v} of ${a.target}">
          <span class="ach-progress-fill" style="width:${pct}%"></span>
          <span class="ach-progress-text">${v.toLocaleString()} / ${a.target.toLocaleString()}</span>
        </span>`;
      }
      return `<li class="${cls}"><span class="ach-icon">${icon}</span><strong>${a.title}</strong><span class="ach-detail">${a.detail}</span>${progressBar}</li>`;
    }).join("");
  }

  private template(): string {
    return `
      <h3>Settings</h3>
      <label class="row">
        <span>Quality</span>
        <select data-bind="quality">
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
      </label>
      <label class="row">
        <span>Volume</span>
        <input type="range" min="0" max="1" step="0.05" data-bind="audioVolume" />
      </label>
      <label class="row checkbox">
        <input type="checkbox" data-bind="muted" />
        <span>Mute audio</span>
      </label>
      <label class="row checkbox">
        <input type="checkbox" data-bind="reducedMotion" />
        <span>Reduced motion</span>
      </label>
      <label class="row checkbox">
        <input type="checkbox" data-bind="colorblindSafe" />
        <span>Colorblind-safe palette</span>
      </label>
      <label class="row checkbox">
        <input type="checkbox" data-bind="practice" />
        <span>Practice mode (no time burn, no submit)</span>
      </label>
      <label class="row checkbox">
        <input type="checkbox" data-bind="gyro" />
        <span>Tilt to steer (mobile gyro)</span>
      </label>
      <label class="row checkbox">
        <input type="checkbox" data-bind="daily" />
        <span>Daily challenge (today's seed, local only)</span>
      </label>
      <label class="row">
        <span>Ship</span>
        <select data-bind="shipVariant">
          ${this.shipOptionsHtml()}
        </select>
      </label>
      <label class="row">
        <span>Hue shift</span>
        <input type="range" min="0" max="360" step="5" data-bind="hueShift" />
      </label>
      <label class="row">
        <span>Trail</span>
        <select data-bind="trailVariant">
          ${this.trailOptionsHtml()}
        </select>
      </label>
      <label class="row checkbox">
        <input type="checkbox" data-bind="ghost" />
        <span>Race your last run (ghost ship)</span>
      </label>
      <label class="row checkbox">
        <input type="checkbox" data-bind="speedrun" />
        <span>Speedrun: race to 10,000</span>
      </label>
      <label class="row checkbox">
        <input type="checkbox" data-bind="modifiers" />
        <span>Random modifier per run</span>
      </label>
      <label class="row checkbox">
        <input type="checkbox" data-bind="endless" />
        <span>Endless mode (no time burn, separate leaderboard)</span>
      </label>
      <details class="achievements-block">
        <summary>Achievements <span class="ach-count">${this.achievementCount()}</span></summary>
        <ul class="achievement-list">${this.achievementListHtml()}</ul>
      </details>
      <details class="achievements-block">
        <summary>Codex</summary>
        <ul class="codex-list">${codexHtml()}</ul>
      </details>
      <div class="settings-io">
        <button type="button" class="btn ghost small" data-action="export-settings">Export settings</button>
        <button type="button" class="btn ghost small" data-action="import-settings">Import settings</button>
      </div>
      <button class="settings-close" data-action="close">Close</button>
    `;
  }

  private wire() {
    const sel = this.panel.querySelector<HTMLSelectElement>('[data-bind="quality"]')!;
    const vol = this.panel.querySelector<HTMLInputElement>('[data-bind="audioVolume"]')!;
    const mute = this.panel.querySelector<HTMLInputElement>('[data-bind="muted"]')!;
    const rm = this.panel.querySelector<HTMLInputElement>('[data-bind="reducedMotion"]')!;
    const cb = this.panel.querySelector<HTMLInputElement>('[data-bind="colorblindSafe"]')!;
    const pr = this.panel.querySelector<HTMLInputElement>('[data-bind="practice"]')!;
    const gy = this.panel.querySelector<HTMLInputElement>('[data-bind="gyro"]')!;
    const dy = this.panel.querySelector<HTMLInputElement>('[data-bind="daily"]')!;

    sel.value = this.settings.quality;
    vol.value = String(this.settings.audioVolume);
    mute.checked = this.settings.muted;
    rm.checked = this.settings.reducedMotion;
    cb.checked = this.settings.colorblindSafe;
    pr.checked = this.settings.practice;
    gy.checked = this.settings.gyro;
    dy.checked = this.settings.daily;

    sel.addEventListener("change", () => this.patch({ quality: sel.value as QualityPreset }));
    vol.addEventListener("input", () => this.patch({ audioVolume: parseFloat(vol.value) }));
    mute.addEventListener("change", () => this.patch({ muted: mute.checked }));
    rm.addEventListener("change", () => this.patch({ reducedMotion: rm.checked }));
    cb.addEventListener("change", () => this.patch({ colorblindSafe: cb.checked }));
    pr.addEventListener("change", () => this.patch({ practice: pr.checked }));
    gy.addEventListener("change", () => this.patch({ gyro: gy.checked }));
    dy.addEventListener("change", () => this.patch({ daily: dy.checked }));

    const sv = this.panel.querySelector<HTMLSelectElement>('[data-bind="shipVariant"]');
    if (sv) {
      sv.value = this.settings.shipVariant;
      sv.addEventListener("change", () => this.patch({ shipVariant: sv.value }));
    }
    const gh = this.panel.querySelector<HTMLInputElement>('[data-bind="ghost"]');
    if (gh) {
      gh.checked = this.settings.ghost;
      gh.addEventListener("change", () => this.patch({ ghost: gh.checked }));
    }
    const sr = this.panel.querySelector<HTMLInputElement>('[data-bind="speedrun"]');
    if (sr) {
      sr.checked = this.settings.speedrun;
      sr.addEventListener("change", () => this.patch({ speedrun: sr.checked }));
    }
    const mods = this.panel.querySelector<HTMLInputElement>('[data-bind="modifiers"]');
    if (mods) {
      mods.checked = this.settings.modifiers;
      mods.addEventListener("change", () => this.patch({ modifiers: mods.checked }));
    }
    const en = this.panel.querySelector<HTMLInputElement>('[data-bind="endless"]');
    if (en) {
      en.checked = this.settings.endless;
      en.addEventListener("change", () => this.patch({ endless: en.checked }));
    }
    const hs = this.panel.querySelector<HTMLInputElement>('[data-bind="hueShift"]');
    if (hs) {
      hs.value = String(this.settings.hueShift);
      hs.addEventListener("input", () => this.patch({ hueShift: parseInt(hs.value, 10) || 0 }));
    }
    const tv = this.panel.querySelector<HTMLSelectElement>('[data-bind="trailVariant"]');
    if (tv) {
      tv.value = this.settings.trailVariant;
      tv.addEventListener("change", () => this.patch({ trailVariant: tv.value }));
    }

    this.panel.querySelector<HTMLButtonElement>('[data-action="close"]')!.addEventListener("click", () => this.toggle());

    // Settings export/import — copy current to clipboard, or paste JSON in.
    const exportBtn = this.panel.querySelector<HTMLButtonElement>('[data-action="export-settings"]');
    if (exportBtn) {
      exportBtn.addEventListener("click", async () => {
        const json = JSON.stringify(this.settings, null, 2);
        try {
          await navigator.clipboard.writeText(json);
          exportBtn.textContent = "Copied!";
          setTimeout(() => (exportBtn.textContent = "Export settings"), 1200);
        } catch {
          window.prompt("Copy your settings JSON:", json);
        }
      });
    }
    const importBtn = this.panel.querySelector<HTMLButtonElement>('[data-action="import-settings"]');
    if (importBtn) {
      importBtn.addEventListener("click", () => {
        const raw = window.prompt("Paste settings JSON:");
        if (!raw) return;
        try {
          const parsed = JSON.parse(raw);
          if (typeof parsed !== "object" || parsed === null) throw new Error("not an object");
          // Only accept known keys to avoid pasting arbitrary data into storage.
          const known = Object.keys(this.settings) as (keyof Settings)[];
          const filtered: Partial<Settings> = {};
          for (const k of known) {
            if (k in parsed) (filtered as Record<string, unknown>)[k] = parsed[k];
          }
          this.patch(filtered);
          importBtn.textContent = "Imported!";
          setTimeout(() => (importBtn.textContent = "Import settings"), 1200);
          // Re-mount drawer state to pick up new values in inputs.
          location.reload();
        } catch {
          importBtn.textContent = "Invalid JSON";
          setTimeout(() => (importBtn.textContent = "Import settings"), 1200);
        }
      });
    }
  }

  private patch(p: Partial<Settings>) {
    this.settings = { ...this.settings, ...p };
    saveSettings(this.settings);
    this.cb.onChange(this.settings);
  }
}

export { profileFor };

function gearSvg(): string {
  return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>`;
}
